import { FC, useState, useMemo } from 'react';
import cls from 'clsx';
import { CurrencyCurrency } from '@linktivity/ars-neo';
import { Dropdown } from '@linktivity/link-ui';
import { useHeaderContext } from './context';
import styles from './currency.module.css';

export interface CurrencyItem {
  code: CurrencyCurrency | string;
  symbol: string;
}

const DEFAULT_CURRENCY_ITEM_LIST = [
  { code: CurrencyCurrency.Jpy, symbol: '¥' },
  { code: CurrencyCurrency.Usd, symbol: '$' },
  { code: CurrencyCurrency.Cny, symbol: '¥' },
  { code: CurrencyCurrency.Twd, symbol: '$' },
  { code: CurrencyCurrency.Krw, symbol: '₩' }
];

const DEFAULT_CURRENCY_LIST = DEFAULT_CURRENCY_ITEM_LIST.map(item => item.code);

interface CurrencyProps {
  /**
   * List of available currencies
   * @default [JPY, USD, CNY, TWD, KRW]
   */
  supportedCurrencies?: CurrencyCurrency[];
  /**
   * Currently selected currency
   */
  currentCurrency?: CurrencyCurrency;
  /**
   * Callback fired when currency changes
   */
  onChange?: (currency: CurrencyCurrency) => void;
}

/**
 * Currency selector component
 * Displays a dropdown to select currency from the list.
 */
const Currency: FC<CurrencyProps> = ({
  supportedCurrencies = DEFAULT_CURRENCY_LIST,
  currentCurrency,
  onChange
}) => {
  const [openMenu, setOpenMenu] = useState(false);
  const { closeMobileMenu } = useHeaderContext();

  const supportedCurrenciesList = useMemo(() => {
    if (supportedCurrencies && supportedCurrencies.length > 0) {
      return supportedCurrencies;
    }
    return DEFAULT_CURRENCY_LIST;
  }, [supportedCurrencies]);

  const currencyList = supportedCurrenciesList.map(currency => {
    const currencyItem = DEFAULT_CURRENCY_ITEM_LIST.find(
      item => item.code === currency
    );
    return {
      code: currency,
      symbol: currencyItem?.symbol || currency
    };
  });

  const currentCurrencyItem = currencyList.find(
    item => item.code === currentCurrency
  );

  // Fallback to first currency if none selected
  const activeCurrency = useMemo(
    () =>
      currentCurrencyItem || currencyList[0] || DEFAULT_CURRENCY_ITEM_LIST[0],
    [currentCurrencyItem, currencyList]
  );

  const handleCurrencyChange = (currency: CurrencyCurrency) => {
    onChange?.(currency);
    setOpenMenu(false);
    closeMobileMenu?.();
  };

  const currencyButton = useMemo(
    () => (
      <button
        className={styles.navButton}
        type="button"
        onClick={() => setOpenMenu(visible => !visible)}
      >
        <span className={styles.navIcon}>{activeCurrency.symbol}</span>
        <span className={styles.text}>{activeCurrency.code}</span>
      </button>
    ),
    [activeCurrency]
  );

  return (
    <Dropdown
      target={currencyButton}
      visible={openMenu}
      sameWidth={false}
      onClose={() => setOpenMenu(false)}
    >
      <Dropdown.Menu className={styles.dropdownMenu}>
        {currencyList.map(currency => (
          <Dropdown.Item key={currency.code}>
            <button
              className={cls(styles.dropdownButton, {
                [styles.active]: currency.code === activeCurrency.code
              })}
              type="button"
              onClick={() => handleCurrencyChange(currency.code)}
            >
              {currency.symbol} {currency.code}
            </button>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default Currency;
